import { sortBy } from "lodash";
import API from "serviceshift-ui/api-client";

import { EmailRecipient } from "@/EmailQuoteOrInvoice/types";
import { EmailTemplateCustomization } from "@/EmailTemplates/emailTemplate";

const isValidId = (value: unknown): boolean => {
  const num = Number(value);
  return Number.isInteger(num) && num > 0;
};

const RECONCILE_INTERVAL_MS = 3_000;
const RECONCILE_MAX_ATTEMPTS = 13;

const findSignedQuoteDocument = (state, quoteId?: number) => {
  if (!quoteId) return undefined;
  const docs = state.job?.documents || [];
  return docs.find(
    (doc) =>
      typeof doc.name === "string" &&
      doc.name.includes(`quote_${quoteId}_signed`)
  );
};

const scheduleGeneratingReconcile = (
  ctx,
  { jobId, quoteId, placeholder, attempt = 1 }
) => {
  const { dispatch, commit, state } = ctx;
  setTimeout(async () => {
    if (state.job?.id !== jobId) return;

    await dispatch("fetchJob", { jobId });
    const doc = findSignedQuoteDocument(state, quoteId);
    if (doc && doc.thumbnail_processed) return;
    if (attempt >= RECONCILE_MAX_ATTEMPTS) return;

    if (!doc) commit("appendJobDocument", placeholder);
    scheduleGeneratingReconcile(ctx, {
      jobId,
      quoteId,
      placeholder,
      attempt: attempt + 1
    });
  }, RECONCILE_INTERVAL_MS);
};

const buildGeneratingDocument = (jobId: number, quoteId?: number) => ({
  id: -Date.now(),
  job_id: jobId,
  name: quoteId
    ? `Signed quote #${quoteId} — generating…`
    : "Signed quote — generating…",
  url: "",
  download_url: "",
  source: "admin_portal",
  created_by_name: null,
  customer_visible: false,
  created_at: new Date().toISOString(),
  thumbnail: null,
  thumbnail_processed: false,
  generating: true
});

export default {
  approveQuote(ctx, { url, note }) {
    const { dispatch, commit, state } = ctx;
    return API.makeRequest(url, {
      method: "POST",
      data: {
        override_reason: note
      }
    }).then(async (response) => {
      const quoteId = response.data?.id;
      const jobId = await dispatch("refreshJobAndQuote", response.data);
      if (!jobId) return;

      const existing = findSignedQuoteDocument(state, quoteId);
      const placeholder = buildGeneratingDocument(jobId, quoteId);
      if (!existing) commit("appendJobDocument", placeholder);

      if (!existing || !existing.thumbnail_processed) {
        scheduleGeneratingReconcile(ctx, { jobId, quoteId, placeholder });
      }
    });
  },
  addItem({ dispatch }, params) {
    const { url, payload } = params;
    return dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(url, {
        method: "POST",
        data: payload
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  createQuote(_, { customerId, data }) {
    return API.makeRequest(`/v2/customers/${customerId}/quotes`, {
      method: "POST",
      data,
      params: {
        item_grouping: 1
      }
    });
  },
  createJobQuote({ dispatch }, { create_url, data }) {
    return API.makeRequest(create_url, {
      method: "POST",
      data
    }).then((response) => {
      const quote = response.data;
      dispatch("fetchJob", {
        jobId: quote.job_id
      });
      return quote;
    });
  },
  callUpdateItem({ dispatch }, item) {
    return API.makeRequest(item.links.line_item_update, {
      method: "PUT",
      data: item
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  fetchDiscounts({ commit }) {
    return API.quote.getDiscounts().then((res) => {
      commit("replace", {
        state: "discounts",
        data: res.data
      });
    });
  },
  fetchQuote(_, { quoteId, customerId }) {
    return API.makeRequest(`/v2/customers/${customerId}/quotes/${quoteId}`, {
      method: "GET",
      params: {
        item_grouping: 1
      }
    });
  },
  fetchTasks({ commit }, params) {
    return API.serviceCategories.getTasks(params).then((res) => {
      commit("replace", {
        state: "tasks",
        data: res.data
      });
    });
  },
  removeItem({ dispatch }, url) {
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(url, {
        method: "DELETE"
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  setQuoteOwnerAndCommissions({ state }, { quoteId, data }) {
    return API.makeRequest(`/v2/quotes/${quoteId}/commissions`, {
      method: "PUT",
      data,
      params: { item_grouping: 1 }
    }).then((res) => {
      const { job } = state;
      if (job && job.id) {
        // Update quote in job quotes
        job.quotes = job.quotes.map((quote) => {
          if (quote.id === quoteId) {
            quote = res.data;
          }
          return quote;
        });
      }
    });
  },
  swapMembershipProgram({ dispatch }, quote) {
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(quote.links.quote_pricing, {
        method: "PUT",
        data: {
          actual_membership_program_id: quote.comparison_membership_program_id,
          comparison_membership_program_id: quote.actual_membership_program_id
        }
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  updateItem({ dispatch }, item) {
    dispatch("confirmQuoteChange").then(() => {
      return dispatch("callUpdateItem", item);
    });
  },
  updateItemNoConfirm({ dispatch }, item) {
    return dispatch("callUpdateItem", item);
  },
  updateDiscount({ dispatch }, params) {
    const { quote_update_url, discountId } = params;

    return API.makeRequest(quote_update_url, {
      method: "PUT",
      data: {
        discount_id: discountId
      }
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  updateQuoteMembershipProgram({ dispatch }, params) {
    const { quote, data } = params;
    return API.makeRequest(quote.links.quote_pricing, {
      method: "PUT",
      data
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  updateLateFee({ dispatch }, params) {
    const { quote, lateFee } = params;
    dispatch("confirmQuoteChange").then(() => {
      return API.makeRequest(quote.links.quote_update, {
        method: "PUT",
        data: { late_fee_cents: lateFee }
      }).then((response) => {
        dispatch("refreshJobAndQuote", response.data);
      });
    });
  },
  async updateQuoteDetails({ dispatch }, { link, data }) {
    const res = await API.makeRequest(link, { method: "PUT", data });
    dispatch("setCurrentQuote", res.data);
    return res.data;
  },
  updateQuote({ dispatch, state }, data) {
    return API.makeRequest(`${state.currentQuote.links.quote_update}`, {
      method: "PUT",
      data
    }).then((res) => {
      dispatch("refreshJobAndQuote", res.data);
    });
  },
  updateQuoteDisplayMembershipSavings(
    { dispatch },
    { url, display_membership_savings }
  ) {
    return API.makeRequest(url, {
      method: "PUT",
      data: { display_membership_savings }
    }).then((res) => {
      dispatch("refreshJobAndQuote", res.data);
    });
  },
  confirmQuoteChange({ commit, state }, quote) {
    return new Promise((resolve, reject) => {
      const currentQuote = state.currentQuote || quote;
      if (!currentQuote || currentQuote.status !== "approved") {
        //confirm status here
        return resolve(void 0);
      }
      commit("setupConfirm", { resolve, reject });
      commit("toggleConfirmChanges", {
        show: true,
        title: "Confirm",
        message:
          "If you continue, this quote will no longer have customer approval. \nA new customer signature will be required."
      });
    });
  },
  setQuoteState({ dispatch }, state_change_url) {
    return API.makeRequest(state_change_url, {
      method: "PUT"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  cloneQuote({ dispatch }, clone_url) {
    return API.makeRequest(clone_url, {
      method: "POST"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  declineQuote({ dispatch }, { decline_url, reason }) {
    return API.makeRequest(decline_url, {
      method: "POST",
      data: {
        reason
      }
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  setCurrentQuote({ commit }, quote) {
    commit("replace", {
      state: "currentQuote",
      data: quote
    });
  },
  async refreshCurrentQuote({ dispatch, state }) {
    if (!state.currentQuote) return;

    const quoteId = state.currentQuote.id;
    const customerId = state.currentQuote.customer_id;
    if (!isValidId(quoteId) || !isValidId(customerId)) {
      window.Sentry?.captureMessage(
        `refreshCurrentQuote skipped: quoteId=${quoteId} customerId=${customerId}`
      );
      return;
    }

    const response = await dispatch("fetchQuote", { quoteId, customerId });
    dispatch("refreshJobAndQuote", response.data);
  },
  refreshJobAndQuote({ dispatch, state }, quote) {
    const jobId = state.job.id || quote.job_id;
    const refetch = jobId ? dispatch("fetchJob", { jobId }) : Promise.resolve();
    if (state.currentQuote) {
      dispatch("setCurrentQuote", quote);
    }
    return refetch.then(() => jobId);
  },
  async regroupItems({ dispatch }, { items, url }) {
    const response = await API.makeRequest(`${url}`, {
      method: "PUT",
      data: items
    });
    dispatch("refreshJobAndQuote", response.data);
  },
  unapproveQuoteSignature({ dispatch }, break_url) {
    return API.makeRequest(break_url, {
      method: "POST"
    }).then((response) => {
      dispatch("refreshJobAndQuote", response.data);
    });
  },
  async getEmailRecipientsForQuote(
    { dispatch },
    params: {
      id: number;
      customer_id: number;
    }
  ) {
    const quoteResponse = await dispatch("fetchQuote", {
      quoteId: params.id,
      customerId: params.customer_id
    });
    const availableContactsResponse = await API.makeRequest<{
      emails: {
        required: {
          email_address: string;
          owner: string;
          label: string | null;
          checked: boolean;
        }[];
        optional: {
          email_address: string;
          owner: string;
          label: string | null;
          checked: boolean;
        }[];
      };
    }>(
      `/v2/customers/${params.customer_id}/addresses/${quoteResponse.data.service_address_id}/contact`
    );
    return [
      ...sortBy(
        availableContactsResponse.data.emails.required.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: true,
            name: email.label || "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      ),
      ...sortBy(
        availableContactsResponse.data.emails.optional.map((email) => {
          return new EmailRecipient({
            owner: email.owner as any,
            primary: false,
            name: email.label || "",
            email_address: email.email_address,
            checked: email.checked
          });
        }),
        "email"
      )
    ] as EmailRecipient[];
  },
  async emailQuote(
    _,
    params: {
      link: string;
      email_addresses?: string[];
      customizations?: EmailTemplateCustomization[];
    }
  ) {
    return API.makeRequest(params.link, {
      method: "POST",
      data: {
        additional_emails: params.email_addresses || [],
        customizations: (params.customizations || []).map((customization) => ({
          key: customization.key,
          value: customization.value
        }))
      }
    });
  }
};
